import React, { FocusEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import Typography from 'carbon-react/lib/components/typography';
import Textbox from 'carbon-react/lib/components/textbox';
import Password from 'carbon-react/lib/components/password';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';

import { AnyError } from '@futrli/errors';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { IMAGE_URLS, UISystemIcon } from '@futrli/ui-components';
import {
  HostedUIIdentityProvider,
  interpolatePathParams,
  PREDICT_PRODUCT_SLUG,
  ROUTE_PARAMS,
  useWindowSize
} from '@futrli/shared-services';

import {
  DESKTOP_LAYOUT_LIMIT,
  FORGOT_PASSWORD_ROUTE,
  FORM_FIELDS,
  LOGIN_FORM_ID,
  REGISTER_ROUTE
} from '../../core/constants';
import { AuthError } from '../../core/types';
import { HandleFederatedSignIn } from '../../federated-sign-in/use-federated-sign-in';
import styles from '../../core/common.scss';
import { getFormValidator } from '../../utils/validation-utils';
import { getErrorMessage } from '../../utils/login';

interface LoginFormData {
  username: string;
  password: string;
}

interface Props {
  disabledEmail?: boolean;
  initialUsername: string;
  cognitoSignInError: AuthError;
  federatedSignInError?: AnyError;
  isLoading: boolean;
  onFederatedSignIn: HandleFederatedSignIn;
  onSubmit: (data: LoginFormData) => void;
}

type LoginTouchedValues = Record<string, boolean>;

type LoginErrorData = Record<string, ReactNode>;

export const LoginForm = ({
  disabledEmail,
  initialUsername,
  cognitoSignInError,
  federatedSignInError,
  isLoading,
  onFederatedSignIn,
  onSubmit
}: Props) => {
  const [touchedValues, setTouchedValues] = useState<LoginTouchedValues>({
    username: false,
    password: false
  });

  const [formValues, setFormValues] = useState<LoginFormData>({
    username: initialUsername,
    password: ''
  });

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  const intl = useIntl();

  const validate = getFormValidator([FORM_FIELDS.username, FORM_FIELDS.password], LOGIN_FORM_ID, intl);

  const [errors, setErrors] = useState<LoginErrorData>(validate((formValues as unknown) as Record<string, string>));

  const navigate = useNavigate();

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormValues({
        ...formValues,
        [name]: value
      });
      setErrors(
        validate({
          ...formValues,
          [name]: value
        })
      );
      if (!touchedValues[name]) {
        setTouchedValues({
          ...touchedValues,
          [name]: true
        });
      }
    },
    [formValues, validate, touchedValues]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { name } = e.target;
      if (!touchedValues[name]) {
        setTouchedValues({
          ...touchedValues,
          [name]: true
        });
      }
    },
    [touchedValues]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const disabled = isLoading || Object.keys(errors).length > 0;
    if (e.key === 'Enter' && !disabled) {
      e.preventDefault();
      onSubmit({ ...formValues });
    }
  };

  const handleRegisterClick = useCallback(
    () => navigate(interpolatePathParams(REGISTER_ROUTE, { [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG })),
    [navigate]
  );

  const handleForgotPasswordClick = useCallback(() => {
    navigate(FORGOT_PASSWORD_ROUTE);
  }, [navigate]);

  const handleSubmit = (_e: React.MouseEvent) => onSubmit({ ...formValues });

  const handleFederatedSignIn = (customProvider: keyof typeof HostedUIIdentityProvider) => {
    return (event: React.MouseEvent) => {
      event.preventDefault();

      onFederatedSignIn(customProvider);
    };
  };

  const dataError = Object.keys(errors).length > 0;

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const errors = validate({ ...formValues });
      if (Object.keys(errors).length === 0) {
        onSubmit({ ...formValues });
      }
    },
    [formValues, onSubmit, validate]
  );

  return (
    <main className={styles.newPageContainer}>
      <div className={classNames(styles.newPageContent, styles.mobileHeight)}>
        <div className={styles.registerFormContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <UISystemIcon iconName="dark-by-sage-logo" width={202} height={90} />
          </div>
          <Typography variant="h1" textAlign="center" marginBottom={16}>
            <FormattedMessage id="login.log_in_to_Futrli" defaultMessage="Log in to Futrli" />
          </Typography>
          <Typography fontSize="16px" textAlign="center" mb={40}>
            <FormattedMessage
              id="login.save_time_and_bookmark_this_page_for_next_time"
              defaultMessage="Save time and bookmark this page for next time."
            />
          </Typography>

          <form onSubmit={handleFormSubmit} noValidate>
            <Textbox
              id="email"
              label={intl.formatMessage({ id: 'common.email_address', defaultMessage: 'Email address' })}
              placeholder={intl.formatMessage({
                id: 'common.enter_email_address',
                defaultMessage: 'Enter your email address'
              })}
              value={formValues.username}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              name={FORM_FIELDS.username}
              error={!!errors.username && touchedValues.username ? (errors.username as string) : ''}
              disabled={isLoading || disabledEmail}
              mb={16}
              size="large"
            />
            <Password
              id="password"
              placeholder={intl.formatMessage({
                id: 'login.password_placeholder',
                defaultMessage: 'Enter a password (9 characters, special, upper, lower, number)'
              })}
              label={intl.formatMessage({ id: 'common.password', defaultMessage: 'Password' })}
              disabled={isLoading}
              value={formValues.password}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              name={FORM_FIELDS.password}
              error={!!errors.password && touchedValues.password ? (errors.password as string) : ''}
              size="large"
            />
            {getErrorMessage(cognitoSignInError, federatedSignInError, true)}
            <Button
              fullWidth
              className={styles.newSubmitButton}
              disabled={isLoading || dataError}
              mb={16}
              mt={24}
              onClick={handleSubmit}
              buttonType="primary"
              size="large"
            >
              <FormattedMessage id="common.continue" defaultMessage="Continue" />
            </Button>
          </form>

          <Typography fontSize="16px" textAlign="center" marginBottom={16}>
            <FormattedMessage id="common.or" defaultMessage="or" />
          </Typography>
          <Button
            buttonType="secondary"
            className={styles.newXeroButtonSageCarbon}
            fullWidth
            disabled={isLoading}
            onClick={handleFederatedSignIn(HostedUIIdentityProvider.Xero)}
          >
            <Typography fontWeight="500" mb={0}>
              <img src={IMAGE_URLS.XERO_LOGO} alt="xero-logo" />
              <FormattedMessage id="login.sign_in_with_xero" defaultMessage="Sign in with Xero" />
            </Typography>
          </Button>
          <div style={{ marginTop: 40, display: 'flex' }}>
            <svg width="100%" height="1" viewBox="0 0 400 1" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0.5H400" stroke="#7F7F7F" strokeWidth="0.5" />
            </svg>
          </div>
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <Link onClick={handleForgotPasswordClick} className={styles.sageCarbonAuthLink}>
              <FormattedMessage id="login.forgot_password" defaultMessage="Forgot password?" />
            </Link>
          </div>
          <Typography fontSize="16px" mt={16}>
            <FormattedMessage id="login.new_user" defaultMessage="New user?" />{' '}
            <Link onClick={handleRegisterClick} className={styles.sageCarbonAuthLink}>
              <FormattedMessage id="login.create_account" defaultMessage="Create account" />
            </Link>
          </Typography>
        </div>
      </div>
    </main>
  );
};
